const TIMEZONE = 'Asia/Tbilisi';

/**
 * Which locale dates are written in.
 *
 * Kept in step with the chosen language by I18nProvider rather than read from
 * the browser: a counter machine set up in English would otherwise print
 * "5 Oct 2026" on a Russian screen. Money and point counts deliberately keep
 * one convention in both languages — the receipt, the till and the screen have
 * to show the same figure, and the grouping separator is not what anyone came
 * here to read.
 */
const DATE_LOCALES = { ru: 'ru-RU', en: 'en-GB' } as const;
let dateLocale: string = DATE_LOCALES.ru;

export function setFormatLanguage(language: keyof typeof DATE_LOCALES): void {
  dateLocale = DATE_LOCALES[language];
}

export function formatGel(tetri: number, locale = 'en-GB'): string {
  const negative = tetri < 0;
  const abs = Math.abs(tetri);
  const major = new Intl.NumberFormat(locale).format(Math.floor(abs / 100));
  const minor = String(abs % 100).padStart(2, '0');
  return `${negative ? '-' : ''}₾${major}.${minor}`;
}

export const formatGelShort = (tetri: number, locale = 'en-GB'): string =>
  `₾${new Intl.NumberFormat(locale).format(Math.round(tetri / 100))}`;

export const formatPoints = (points: number, locale = 'en-GB'): string =>
  new Intl.NumberFormat(locale).format(points);

export const formatRate = (basisPoints: number): string => `${basisPoints / 100}%`;

export const formatMultiplier = (milli: number): string =>
  `${(milli / 1000).toFixed(milli % 1000 === 0 ? 0 : 2)}x`;

export function formatDateTime(value: string | Date, locale = dateLocale): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: TIMEZONE,
  }).format(typeof value === 'string' ? new Date(value) : value);
}

/**
 * A time on today's board, said the way the floor says it.
 *
 * "18 Oct 2026, 20:10" is correct and useless when the car is due in two
 * hours: the year is noise and the date is the one everybody is standing in.
 * Today gives just the clock, tomorrow says so, anything further keeps a date.
 */
export function formatWhen(value: string | Date, locale = dateLocale): string {
  const when = typeof value === 'string' ? new Date(value) : value;
  const clock = new Intl.DateTimeFormat(locale, {
    hour: '2-digit', minute: '2-digit', timeZone: TIMEZONE,
  }).format(when);

  const day = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE }).format(date);
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 86_400_000);

  const at = AT[dateLocale] ?? 'at';
  if (day(when) === day(today)) return `${at} ${clock}`;
  if (day(when) === day(tomorrow)) return `${TOMORROW[dateLocale] ?? 'tomorrow'} ${at} ${clock}`;

  const date = new Intl.DateTimeFormat(locale, {
    day: 'numeric', month: 'short', timeZone: TIMEZONE,
  }).format(when);
  return `${date} ${at} ${clock}`;
}

const TOMORROW: Record<string, string> = { 'ru-RU': 'завтра', 'en-GB': 'tomorrow' };
const AT: Record<string, string> = { 'ru-RU': 'в', 'en-GB': 'at' };

export function formatDate(value: string | Date, locale = dateLocale): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeZone: TIMEZONE,
  }).format(typeof value === 'string' ? new Date(value) : value);
}
